import { useEffect, useMemo, useState } from 'react';
import { Api } from '../../application/panelApi';
import { errText, fmtDate, nf, plural, sizeStr } from '../../lib/format';
import { openProfileCard } from './ProfileCard';
import { ClusterPill } from './shared';
import type { ClusterProfileStats, NodeCluster, ProfileListItem } from './model';
import type { OverviewRow } from './useProfilesOverview';

type OverviewTabProps = {
  active: boolean;
  cid: string;
  setCid: (cid: string) => void;
  clusters: NodeCluster[];
  rows: OverviewRow[] | null;

  version: number;
  control: boolean;
  engineChromium: number;
};

const PHASES: Array<[string, string, string]> = [
  ['new', 'Новые', 'Новый'],
  ['warming', 'Греются', 'Греется'],
  ['ready', 'Готовы', 'Готов'],
  ['targeting', 'В задачах', 'В задачах'],
  ['retired', 'Списаны', 'Списан'],
];

const PHASE_ONE: Record<string, string> = Object.fromEntries(
  PHASES.map(([id, , one]) => [id, one]),
);

const STATUS_LABEL: Record<string, string> = {
  created: 'создан',
  ready: 'ждёт',
  idle: 'ждёт',
  running: 'браузер открыт',
  failed: 'ошибка',
  completed: 'завершён',
};

const PAGE = 200;

const osOf = (c: NodeCluster) => (c.os === 'windows' ? 'windows' : 'linux');

export function OverviewTab({
  active,
  cid,
  setCid,
  clusters,
  rows,
  version,
  control,
  engineChromium,
}: OverviewTabProps) {
  const [filter, setFilter] = useState('');
  const [phase, setPhase] = useState('all');
  const [limit, setLimit] = useState(PAGE);
  const [list, setList] = useState<{ cid: string; items: ProfileListItem[] } | null>(null);
  const [listErr, setListErr] = useState<{ cid: string; msg: string } | null>(null);

  const nodes: OverviewRow[] = rows ?? clusters.map((c) => ({ c, prof: {}, stale: true }));
  const selectedId = nodes.some((r) => r.c.id === cid) ? cid : (nodes[0]?.c.id ?? '');
  const selected = nodes.find((r) => r.c.id === selectedId);

  useEffect(() => {
    if (!control || !selectedId) return undefined;
    let alive = true;
    void (async () => {
      try {
        const data = (await Api.clusters.profileList(selectedId)) as {
          profiles?: ProfileListItem[];
        };
        if (!alive) return;
        setListErr(null);
        setList({ cid: selectedId, items: data.profiles || [] });
      } catch (err) {
        if (alive) setListErr({ cid: selectedId, msg: errText(err) });
      }
    })();
    return () => {
      alive = false;
    };
  }, [selectedId, version, control]);

  const items = list && list.cid === selectedId ? list.items : null;
  const counts = useMemo(() => {
    const out: Record<string, number> = { all: items?.length ?? 0, failed: 0 };
    for (const p of items ?? []) {
      out[p.phase] = (out[p.phase] ?? 0) + 1;
      if (p.status === 'failed') out.failed! += 1;
    }
    return out;
  }, [items]);

  const q = filter.trim().toLowerCase();
  const shown = useMemo(
    () =>
      (items ?? []).filter((p) => {
        if (phase === 'failed' ? p.status !== 'failed' : phase !== 'all' && p.phase !== phase)
          return false;
        return (
          !q ||
          `${p.id} ${p.status} ${p.phase} ${PHASE_ONE[p.phase] ?? ''} ${STATUS_LABEL[p.status] ?? ''}`
            .toLowerCase()
            .includes(q)
        );
      }),
    [items, phase, q],
  );

  const pick = (id: string) => {
    setCid(id);
    setFilter('');
    setPhase('all');
    setLimit(PAGE);
  };
  const pickPhase = (id: string) => {
    setPhase(phase === id ? 'all' : id);
    setLimit(PAGE);
  };

  if (!control) {
    return (
      <div className={`pr-pane${active ? '' : ' hidden'}`} data-prpane="overview">
        <div className="empty">Профили хранятся на нодах, а панель сейчас к ним не подключена.</div>
      </div>
    );
  }
  if (!clusters.length) {
    return (
      <div className={`pr-pane${active ? '' : ' hidden'}`} data-prpane="overview">
        <div className="empty">Серверов пока нет</div>
      </div>
    );
  }

  const prof: ClusterProfileStats = selected?.prof ?? {};
  let body;
  if (!items) {
    body = (
      <div className="empty">
        {listErr && listErr.cid === selectedId ? listErr.msg : 'Загрузка…'}
      </div>
    );
  } else if (!shown.length) {
    body = <div className="empty">Профилей не найдено</div>;
  } else {
    body = (
      <>
        <div className="scroll pf-scroll">
          <table className="pf-table">
            <thead>
              <tr>
                <th>Профиль</th>
                <th>Фаза</th>
                <th>Состояние</th>
                <th className="num">Дней подготовки</th>
                <th className="num">Прогонов</th>
                <th>Создан</th>
              </tr>
            </thead>
            <tbody id="prListBody">
              {shown.slice(0, limit).map((p) => (
                <tr
                  key={p.id}
                  data-id={p.id}
                  onClick={() => openProfileCard(selectedId, p.id, engineChromium)}
                >
                  <td className="mono">{p.id}</td>
                  <td>
                    <span className={`pf-phase ${p.phase}`}>{PHASE_ONE[p.phase] ?? p.phase}</span>
                  </td>
                  <td>
                    <span className={`pf-status ${p.status}`}>
                      {STATUS_LABEL[p.status] ?? p.status}
                    </span>
                  </td>
                  <td className="num">{p.farmDays ?? 0}</td>
                  <td className="num">{p.runCount ?? 0}</td>
                  <td className="pf-date">{p.createdAt ? fmtDate(p.createdAt) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {shown.length > limit ? (
          <button type="button" className="btn pf-more" onClick={() => setLimit((v) => v + PAGE)}>
            Показать ещё {nf.format(Math.min(PAGE, shown.length - limit))} из{' '}
            {nf.format(shown.length - limit)}
          </button>
        ) : null}
      </>
    );
  }

  return (
    <div className={`pr-pane pf-layout${active ? '' : ' hidden'}`} data-prpane="overview">
      <nav className="sv-list pf-nodes" aria-label="Сервера">
        {nodes.map(({ c, prof: p, stale }) => {
          const total = p.total ?? 0;
          const readyPct = total ? Math.round(((p.ready ?? 0) / total) * 100) : 0;
          return (
            <button
              key={c.id}
              type="button"
              data-profile-cluster={c.id}
              className={`sv-list-item pf-node${c.id === selectedId ? ' on' : ''}`}
              aria-current={c.id === selectedId ? 'true' : undefined}
              onClick={() => pick(c.id)}
            >
              <img src={`/brand/${osOf(c)}.png`} alt="" width={24} height={24} />
              <span className="sv-list-text">
                <b>{c.name}</b>
                <small>
                  {rows
                    ? `${nf.format(total)} ${plural(total, 'профиль', 'профиля', 'профилей')}`
                    : 'Загрузка…'}
                  {stale && rows ? ' · нет связи' : ''}
                </small>
                {rows ? (
                  <span className="pf-mini" title={`Готовы ${readyPct}%`}>
                    <i style={{ width: `${readyPct}%` }} />
                  </span>
                ) : null}
              </span>
            </button>
          );
        })}
      </nav>

      <section className="pf-main">
        {selected ? (
          <header className="pf-head">
            <img src={`/brand/${osOf(selected.c)}.png`} alt="" width={40} height={40} />
            <div className="pf-head-text">
              <h2>
                {selected.c.name}
                <ClusterPill c={selected.c} />
              </h2>
              <p className="sv-facts">
                <span>
                  <b>{nf.format(prof.total ?? 0)}</b>{' '}
                  {plural(prof.total ?? 0, 'профиль', 'профиля', 'профилей')}
                </span>
                <span>
                  <b>{nf.format(prof.active ?? 0)}</b> с открытым браузером
                </span>
                <span>
                  <b>{nf.format(prof.ready ?? 0)}</b> готовы
                </span>
                <span>
                  диск{' '}
                  {prof.diskMb == null ? (
                    '—'
                  ) : (
                    <>
                      <b>{sizeStr(prof.diskMb)}</b>
                      {prof.diskTotalMb == null ? '' : ` из ${sizeStr(prof.diskTotalMb)}`}
                    </>
                  )}
                </span>
              </p>
            </div>
          </header>
        ) : null}

        {items && items.length ? (
          <div className="pf-compo">
            <div className="pf-bar" role="group" aria-label="Состав по фазам">
              {PHASES.filter(([id]) => counts[id]).map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  className={`pf-seg ${id}${phase === id ? ' on' : ''}`}
                  style={{ flexGrow: counts[id] }}
                  title={`${label}: ${nf.format(counts[id] ?? 0)}`}
                  onClick={() => pickPhase(id)}
                />
              ))}
            </div>
            <div className="pf-legend">
              {PHASES.map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  className={`pf-leg ${id}${phase === id ? ' on' : ''}`}
                  disabled={!counts[id]}
                  onClick={() => pickPhase(id)}
                >
                  <i />
                  {label} <b>{nf.format(counts[id] ?? 0)}</b>
                </button>
              ))}
              <button
                type="button"
                className={`pf-leg failed${phase === 'failed' ? ' on' : ''}`}
                disabled={!counts.failed}
                onClick={() => pickPhase('failed')}
              >
                <i />С ошибкой <b>{nf.format(counts.failed ?? 0)}</b>
              </button>
            </div>
          </div>
        ) : null}

        <div className="pf-tools">
          <input
            type="text"
            id="prFilter"
            placeholder="Поиск по номеру профиля или состоянию"
            spellCheck={false}
            value={filter}
            onChange={(e) => {
              setFilter(e.target.value);
              setLimit(PAGE);
            }}
          />
          {items ? (
            <span className="pf-count" id="prListInfo">
              {phase !== 'all' || q
                ? `Найдено ${nf.format(shown.length)} из ${nf.format(items.length)}`
                : `${nf.format(items.length)} в списке`}
            </span>
          ) : null}
          {phase !== 'all' || q ? (
            <button
              type="button"
              className="lp-link"
              onClick={() => {
                setPhase('all');
                setFilter('');
              }}
            >
              Сбросить
            </button>
          ) : null}
        </div>

        {body}
      </section>
    </div>
  );
}
